<template>
  <div class="ponderation-wrapper" data-step="15">
    <div class="ponderation-skills-wrapper">
      <draggable
        v-if="state.ponderation_type === 'coef'"
        :list="state.ponderation_obj"
        :group="{ name: 'g1' }"
        item-key="id"
        class="drag-and-drop-wrapper"
      >
        <template #item="{ element }">
          <div class="ponderation-skill-container">
            <span class="title">{{ element.label }}</span>
            <draggable
              :list="element.skills"
              item-key="id"
              class="skills-wrapper"
              :group="{ name: 'g2' }"
            >
              <template #header v-if="element.skills.length === 0">
                <div class="no-items">
                  Aucune compétence n’a été attribuée à cette colonne.
                </div>
              </template>
              <template #item="{ element }">
                <div
                  class="skill tooltip"
                  :class="{ disabled: !isSkillCompatible(element.id) }"
                >
                  <span
                    class="tooltiptext"
                    v-if="!isSkillCompatible(element.id)"
                  >
                    Compétence non disponible en version Essentielle
                  </span>
                  {{ element.name }}
                </div>
              </template>
            </draggable>
          </div>
        </template>
      </draggable>
      <div v-else class="drag-and-drop-wrapper">
        <div class="ponderation-skill-container">
          <span class="title">Pondération neutre</span>
          <div class="skills-wrapper">
            <div
              v-for="skill in modules.filter(
                (m) => !m.disabled && m.id !== 'motivation'
              )"
              :key="skill.id_v2 ?? skill.id"
              class="skill tooltip"
              :class="{ disabled: !isSkillCompatible(skill.id_v2 ?? skill.id) }"
            >
              <span
                class="tooltiptext"
                v-if="!isSkillCompatible(skill.id_v2 ?? skill.id)"
              >
                Compétence non disponible en version Essentielle
              </span>
              {{ skill.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="buttons-inline">
      <Button
        class="primary sm"
        :label="`Appliquer une pondération ${
          state.ponderation_type === 'neutral' ? 'manuelle' : 'neutre'
        }`"
        @click="
          state.ponderation_type =
            state.ponderation_type === 'neutral' ? 'coef' : 'neutral'
        "
      />
      <Button
        class="green sm"
        label="Appliquer la pondération"
        @click="applyPonderation"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { useCampaignStore } from "@/stores/campaign.js";
import { useModulesStore } from "@/stores/modules.js";
import { useParticipantStore } from "@/stores/participant.js";
import { useAppStore } from "@/stores/app.js";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";
import draggable from "vuedraggable";

const toast = useToast();

const route = useRoute();

const campaignStore =
  route.name === "campaign" ? useCampaignStore() : useOnboardingStore();

const { modules } = useModulesStore();

const participantStore = useParticipantStore();

const appStore = useAppStore();

const currentCampaignVersionID = computed(() => {
  if (!campaignStore.currentCampaign.calibration_id) return 20150;
  if (campaignStore.currentCampaign.calibration_id < 10000) return 20150;
  return campaignStore.currentCampaign.calibration_id;
});

const state = reactive({
  ponderation_type:
    campaignStore.currentCampaign.skills_secondary === "neutral"
      ? "neutral"
      : "coef",
  ponderation_obj: [
    {
      id: "mandatory",
      label: "compétences indispensables",
      skills: modules
        .filter((module) => {
          if (campaignStore.currentCampaign.version === 1) return false;
          if (module.id === "motivation") return false;
          return (
            campaignStore.currentCampaign.skills_mandatory
              .split(";")
              .includes(module.id_v2 ?? module.id) && !module.disabled
          );
        })
        .map((skill) => {
          return {
            id: skill.id_v2 ?? skill.id,
            name: skill.name,
          };
        }),
    },
    {
      id: "important",
      label: "compétences importantes",
      skills: modules
        .filter((module) => {
          if (campaignStore.currentCampaign.version === 1) return false;
          if (module.id === "motivation") return false;
          return (
            campaignStore.currentCampaign.skills_important
              .split(";")
              .includes(module.id_v2 ?? module.id) && !module.disabled
          );
        })
        .map((skill) => {
          return {
            id: skill.id_v2 ?? skill.id,
            name: skill.name,
          };
        }),
    },
    {
      id: "secondary",
      label: "compétences secondaires",
      skills: modules
        .filter((module) => {
          if (campaignStore.currentCampaign.version === 1) return false;
          if (module.id === "motivation") return false;
          return (
            campaignStore.currentCampaign.skills_secondary
              .split(";")
              .includes(module.id_v2 ?? module.id) && !module.disabled
          );
        })
        .map((skill) => {
          return {
            id: skill.id_v2 ?? skill.id,
            name: skill.name,
          };
        }),
    },
    {
      id: "excluded",
      label: "compétences non prises en compte",
      skills: modules
        .filter((module) => {
          if (module.id === "motivation") return false;
          if (campaignStore.currentCampaign.version === 1) {
            return module.id_v2;
          }

          const allSkills = `${campaignStore.currentCampaign.skills_mandatory};${campaignStore.currentCampaign.skills_important};${campaignStore.currentCampaign.skills_secondary}`;

          return (
            !allSkills.split(";").includes(module.id_v2 ?? module.id) &&
            !module.disabled
          );
        })
        .map((skill) => {
          if (campaignStore.currentCampaign.version === 1) {
            return {
              id: skill.id,
              name: skill.name,
            };
          }

          return {
            id: skill.id_v2 ?? skill.id,
            name: skill.name,
          };
        }),
    },
  ],
});

const isSkillCompatible = (skillID) => {
  const compatiblesVersions = skillsCompatibleGameVersions[skillID];
  return (
    !compatiblesVersions ||
    compatiblesVersions.includes(currentCampaignVersionID.value)
  );
};

const skills_mandatory = computed(() => {
  const mandatory_obj = state.ponderation_obj.find(
    (type) => type.id === "mandatory"
  );

  const source =
    state.ponderation_type === "coef" ? mandatory_obj.skills : modules;

  return source.map((skill) => skill.id_v2 ?? skill.id).join(";");
});

const skills_important = computed(() => {
  const important_obj = state.ponderation_obj.find(
    (type) => type.id === "important"
  );

  return state.ponderation_type === "coef"
    ? important_obj.skills.map((skill) => skill.id_v2 ?? skill.id).join(";")
    : "neutral";
});

const skills_secondary = computed(() => {
  const secondary_obj = state.ponderation_obj.find(
    (type) => type.id === "secondary"
  );

  return state.ponderation_type === "coef"
    ? secondary_obj.skills.map((skill) => skill.id_v2 ?? skill.id).join(";")
    : "neutral";
});

async function applyPonderation() {
  appStore.ponderationIsVisible = false;

  let coefs;

  if (campaignStore.currentCampaign.version === 2) {
    await campaignStore.updateCampaign(
      route.params.companyId,
      route.params.spaceId,
      {
        ...campaignStore.currentCampaign,
        skills_mandatory: skills_mandatory.value,
        skills_important: skills_important.value,
        skills_secondary: skills_secondary.value,
      }
    );
  } else {
    const getCoef = (skill) => {
      if (skills_mandatory.value.includes(skill)) return 3;
      if (skills_important.value.includes(skill)) return 2;
      if (skills_secondary.value.includes(skill)) return 1;

      return 0;
    };

    coefs = {
      MS_RDPB: getCoef("MS_RDPB"),
      MS_ADPT: getCoef("MS_ADPT"),
      MS_GDSS: getCoef("MS_GDSS"),
    };
  }

  participantStore.computeCompatibility(coefs);

  toast.success(
    "Pondération appliquée à vos talents, les résultats ont été mis à jour sur votre espace."
  );
}
</script>
